/**
 * This module is responsible for the functionality of the Podcast Navigator component.
 * It handles three main functionalities:
 * 1. Toggling season/episodes via the navigation items
 * 2. Toggling season/episodes via the arrow buttons
 * 3. Opening/closing the episodes container in a modal (for mobile)
 * Note that toggling a season/episode doesn't mean to just show/hide the episode content,
 * it also involves adding/removing the 'active' class from the corresponding navigation item.
 */

document.addEventListener('DOMContentLoaded', function () {

  /**
   * Responsible for handling click on season toggler
   * @param event
   */
  function handleSeasonToggleByEvent(event) {
    const seasonId = event.target.dataset.season;
    const seasonElement = document.getElementById(seasonId);

    // Remove 'active' class from all season togglers and containers
    removeActiveFromAllSeasons();

    // Add 'active' class to the clicked season container
    seasonElement.classList.add('active');

    // Add 'active' class to the clicked season toggler
    event.target.classList.add('active');
  }

  /**
   * Responsible for handling click on episode toggler
   * @param event
   */
  function handleEpisodeToggleByEvent(event) {
    const episodeId = event.target.dataset.episode;
    const episodeElement = document.getElementById(episodeId);

    // Remove 'active' class from all episode togglers and containers
    removeActiveFromAllEpisodes();

    // Add 'active' class to the clicked episode container
    episodeElement.classList.add('active');

    // Add 'active' class to the clicked episode toggler
    event.target.classList.add('active');
  }

  /**
   * Responsible for opening/closing the episodes container and adding/removing necessary event listeners
   * @param event
   */
  function toggleEpisodesContainer(event) {
    const episodesContainer = document.querySelector('.podcasts-episodes__index');
    episodesContainer.classList.toggle('active');

    // If it's open, add event listener to close it on ESC
    if (episodesContainer.classList.contains('active')) {
      document.addEventListener('keydown', closeEpisodesContainerOnEsc);
      event.stopPropagation();
      document.addEventListener('click', closeEpisodesContainerOnClickOut);
    } else {
      document.removeEventListener('keydown', closeEpisodesContainerOnEsc);
      document.removeEventListener('click', closeEpisodesContainerOnClickOut);
    }
  }

  /**
   * Responsible for closing the episodes container on ESC
   * @param event
   */
  function closeEpisodesContainerOnEsc(event) {
    if (event.key === 'Escape') {
      toggleEpisodesContainer();
    }
  }

  /**
   * Responsible for closing episodes container when clicking outside of it
   * @param event
   */
  function closeEpisodesContainerOnClickOut(event) {
    const episodesContainer = document.querySelector('.podcasts-episodes__index');
    if (!episodesContainer.contains(event.target)) {
      episodesContainer.classList.remove('active');
      document.removeEventListener('click', closeEpisodesContainerOnClickOut);
    }
  }

  /**
   * Responsible for toggling the active episode based on direction (next or previous)
   * @param direction
   */
  function handleEpisodeToggleByArrowClick(direction) {
    const activeEpisode = document.querySelector('.podcasts-episodes__index__episode-toggler.active');
    const episodeTogglers = document.querySelectorAll('.podcasts-episodes__index__episode-toggler');
    const currentLoopCounter = parseInt(activeEpisode.dataset.episodeLoopCounter);

    let nextLoopCounter;

    if (direction === 'next') {
      nextLoopCounter = currentLoopCounter + 1;
    } else {
      nextLoopCounter = currentLoopCounter - 1;
    }

    // Ensure the loop counter stays within the valid range
    if (nextLoopCounter < 1) {
      nextLoopCounter = episodeTogglers.length;
    } else if (nextLoopCounter > episodeTogglers.length) {
      nextLoopCounter = 1;
    }

    const nextEpisodeToggler = [...episodeTogglers].find(
      (toggler) => parseInt(toggler.dataset.episodeLoopCounter) === nextLoopCounter
    );

    removeActiveFromAllEpisodes();

    // Add 'active' class to the next or previous episode toggler
    nextEpisodeToggler.classList.add('active');

    // Add 'active' class to the next or previous episode
    const nextEpisode = document.querySelector(`#${nextEpisodeToggler.dataset.episode}`);
    nextEpisode.classList.add('active');

    // Toggle the season if necessary
    handleSeasonToggleByArrowClick(nextEpisodeToggler);
  }

  /**
   * Responsible for toggling the active season if necessary
   * @param episodeElement
   */
  function handleSeasonToggleByArrowClick(episodeElement) {
    const seasonId = episodeElement.dataset.episode.split('-')[1].replace('s', '');

    // Remove 'active' class from all season togglers and containers
    removeActiveFromAllSeasons();

    // Add 'active' class to the season corresponding to the episode
    const targetSeasonToggler = document.querySelector(`#episodes-index-season-toggler-${seasonId}`);
    targetSeasonToggler.classList.add('active');

    const targetSeasonContainer = document.querySelector(`#episodes-index-season-${seasonId}`);
    targetSeasonContainer.classList.add('active');
  }

  /**
   * Responsible for removing the 'active' class from all seasons and season togglers
   */
  function removeActiveFromAllSeasons() {
    // Remove 'active' class from all seasons
    const allSeasons = document.querySelectorAll('.podcasts-episodes__index__season');
    allSeasons.forEach((season) => season.classList.remove('active'));

    // Remove 'active' class from all season togglers
    const allSeasonTogglers = document.querySelectorAll('.podcasts-episodes__index__season-toggler');
    allSeasonTogglers.forEach((seasonToggler) => seasonToggler.classList.remove('active'));
  }

  /**
   * Responsible for removing the 'active' class from all episodes and episode togglers
   */
  function removeActiveFromAllEpisodes() {

    // Remove 'active' class from all episodes
    const allEpisodes = document.querySelectorAll('.podcasts-episodes__data__episode');
    allEpisodes.forEach((episode) => episode.classList.remove('active'));

    // Remove 'active' class from all episode togglers
    const allEpisodeTogglers = document.querySelectorAll('.podcasts-episodes__index__episode-toggler');
    allEpisodeTogglers.forEach((episodeToggler) => episodeToggler.classList.remove('active'));
  }

  // Add event listeners to season and episode togglers
  const seasonTogglers = document.querySelectorAll('.podcasts-episodes__index__season-toggler');
  seasonTogglers.forEach((seasonToggler) => {
    seasonToggler.addEventListener('click', handleSeasonToggleByEvent);
  });

  const episodeTogglers = document.querySelectorAll('.podcasts-episodes__index__episode-toggler');
  episodeTogglers.forEach((episodeToggler) => {
    episodeToggler.addEventListener('click', handleEpisodeToggleByEvent);
  });

  // Add event listeners to the modal togglers
  const episodesContainerOpener = document.querySelector('.podcasts-episodes__open-episodes-container');
  const episodesContainerCloser = document.querySelector('.podcasts-episodes__close-episodes-container');

  episodesContainerOpener.addEventListener('click', toggleEpisodesContainer);
  episodesContainerCloser.addEventListener('click', toggleEpisodesContainer);

  // Add event listeners to the next and previous arrow icons
  const nextEpisodeButton = document.querySelector('.podcasts-episodes__next-episode');
  const previousEpisodeButton = document.querySelector('.podcasts-episodes__previous-episode');

  nextEpisodeButton.addEventListener('click', () => handleEpisodeToggleByArrowClick('next'));
  previousEpisodeButton.addEventListener('click', () => handleEpisodeToggleByArrowClick('previous'));

});
